import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import TableSkeleton from "../../utils/TableSkeleton";
import { customStyles } from "../../utils/DataTableCustomStyle";
import * as Constants from "../../utils/Constants";
import * as UrlConstants from "../../utils/UrlConstants";
import { getClientIdOfUser, getAuthToken, showToast } from "../../utils/utility";
import {
    DEFAULT_PAGE_SIZE,
    extractPaginationHeadersFromFetch,
    PAGE_SIZE_OPTIONS,
} from "../../utils/paginationUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateRight } from "@fortawesome/free-solid-svg-icons";

const STATUS_STYLES = {
    PENDING:     { bg: "#fff8e1", color: "#f9a825" },
    SENT:        { bg: "#e6f4ea", color: "#137333" },
    FAILED:      { bg: "#fce8e6", color: "#c5221f" },
    DEAD_LETTER: { bg: "#f3e5f5", color: "#7b1fa2" },
    // App push alerts held back on purpose (90 s ignition debounce, quiet hours, daily ceiling,
    // mute, no registered phone) — not a failure, never retried.
    SUPPRESSED:  { bg: "#f1f3f4", color: "#5f6368" },
};

const ALL_STATUSES = ["", "PENDING", "SENT", "FAILED", "DEAD_LETTER", "SUPPRESSED"];

const StatusBadge = ({ status }) => {
    const s = STATUS_STYLES[status] || { bg: "#f1f3f4", color: "#5f6368" };
    return (
        <span style={{
            background: s.bg, color: s.color,
            borderRadius: "12px", padding: "2px 10px",
            fontSize: "12px", fontWeight: 600, whiteSpace: "nowrap",
        }}>
            {status?.replace("_", " ")}
        </span>
    );
};

const NotificationHistory = () => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading]             = useState(true);
    const [totalRows, setTotalRows]         = useState(0);
    const [perPage, setPerPage]             = useState(DEFAULT_PAGE_SIZE);
    const [currentPage, setCurrentPage]     = useState(1);
    const [statusFilter, setStatusFilter]   = useState("");
    const [retryingId, setRetryingId]       = useState(null);

    useEffect(() => {
        fetchNotifications(1, perPage, statusFilter);
    }, []);

    const fetchNotifications = (page, size, status) => {
        setLoading(true);
        const clientId = getClientIdOfUser();
        let url = `${Constants.BASE_URL}${Constants.API_V1}/${clientId}/${UrlConstants.NOTIFICATIONS}?page=${page - 1}&size=${size}`;
        if (status) url += `&status=${status}`;

        fetch(url, {
            headers: { Authorization: `${Constants.BEARER} ${getAuthToken()}` },
        })
            .then((res) => {
                const pagination = extractPaginationHeadersFromFetch(res);
                return res.json().then((data) => ({ data, pagination }));
            })
            .then(({ data, pagination }) => {
                const rows = data.responseData?.content || data.responseData || [];
                setNotifications(rows);
                setTotalRows(pagination?.totalCount || rows.length);
            })
            .catch(() => showToast("Failed to load notifications", { type: "error" }))
            .finally(() => setLoading(false));
    };

    const handleStatusChange = (e) => {
        const s = e.target.value;
        setStatusFilter(s);
        setCurrentPage(1);
        fetchNotifications(1, perPage, s);
    };

    const handleRetry = (id) => {
        setRetryingId(id);
        const clientId = getClientIdOfUser();
        fetch(`${Constants.BASE_URL}${Constants.API_V1}/${clientId}/${UrlConstants.NOTIFICATION_RETRY(id)}`, {
            method: "POST",
            headers: { Authorization: `${Constants.BEARER} ${getAuthToken()}` },
        })
            .then((r) => r.json())
            .then(() => {
                showToast("Retry queued", { type: "success" });
                fetchNotifications(currentPage, perPage, statusFilter);
            })
            .catch(() => showToast("Failed to retry", { type: "error" }))
            .finally(() => setRetryingId(null));
    };

    const columns = [
        {
            name: "Alert Type",
            selector: (row) => row.alertType?.replace(/_/g, " "),
            sortable: true,
            wrap: true,
            width: "160px",
        },
        {
            name: "Channel",
            selector: (row) => row.channel,
            width: "100px",
        },
        {
            name: "Status",
            cell: (row) => <StatusBadge status={row.status} />,
            width: "130px",
        },
        {
            name: "Recipient",
            selector: (row) => row.recipient || "—",
            wrap: true,
        },
        {
            name: "Device",
            selector: (row) => row.deviceId || "—",
            width: "120px",
        },
        {
            name: "Attempts",
            selector: (row) => row.attempts ?? 0,
            width: "90px",
            center: true,
        },
        {
            name: "Created",
            selector: (row) => row.createdAt ? new Date(row.createdAt).toLocaleString() : "—",
            sortable: true,
            width: "170px",
        },
        {
            name: "Actions",
            width: "90px",
            center: true,
            cell: (row) => {
                if (row.status !== "DEAD_LETTER") return null;
                const busy = retryingId === row.id;
                return (
                    <button
                        className="btn-sm btn-warning"
                        disabled={busy}
                        title="Retry"
                        onClick={() => handleRetry(row.id)}
                        style={{ fontSize: "12px" }}
                    >
                        <FontAwesomeIcon icon={faRotateRight} spin={busy} />
                        {" "}Retry
                    </button>
                );
            },
        },
    ];

    return (
        <div className="h-full flex flex-col gap-3">
            <div className="shrink-0 flex flex-wrap items-center gap-3 bg-white border border-ink-200 rounded-xl shadow-xs px-4 py-3">
                <label className="text-sm font-medium text-ink-700" htmlFor="notif-status">Filter by status</label>
                <select
                    id="notif-status"
                    value={statusFilter}
                    onChange={handleStatusChange}
                    className="h-9 px-3 pr-8 text-base rounded-md bg-white border border-ink-200 shadow-inset text-ink-900 hover:border-ink-300 focus:border-brand-500 focus:outline-none focus:ring"
                >
                    {ALL_STATUSES.map((s) => (
                        <option key={s} value={s}>{s || "All"}</option>
                    ))}
                </select>
            </div>

            <div className="flex-1 min-h-0 overflow-hidden rounded-xl border border-ink-200 bg-white shadow-xs">
                <DataTable
                    columns={columns}
                    data={notifications}
                    customStyles={customStyles}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationPerPage={perPage}
                    paginationRowsPerPageOptions={PAGE_SIZE_OPTIONS}
                    onChangePage={(page) => {
                        setCurrentPage(page);
                        fetchNotifications(page, perPage, statusFilter);
                    }}
                    onChangeRowsPerPage={(newSize, page) => {
                        setPerPage(newSize);
                        fetchNotifications(page, newSize, statusFilter);
                    }}
                    progressPending={loading}
                    progressComponent={<TableSkeleton />}
                    noDataComponent={
                        <div className="py-12 px-4 text-center text-sm text-ink-500">No trip notifications yet.</div>
                    }
                />
            </div>
        </div>
    );
};

export default NotificationHistory;
